import { Rectangle } from "../../../../maths/Rectangle";
import { Matrix } from "../../../../maths/Matrix";
import { Vector4 } from "../../../../maths/Vector4";
import { Const } from "../../../../Const";
import { BlendMode, BlendModeHandler } from "../../../../webgl/canvas/BlendMode";
import { Stat } from "../../../../utils/Stat";
import { ShaderDefines2D } from "../../../../webgl/shader/d2/ShaderDefines2D";
const _DefaultClipInfo = {
    clipMatrix: new Matrix(),
    clipMatDir: new Vector4(Const.MAX_CLIP_SIZE, 0, 0, Const.MAX_CLIP_SIZE),
    clipMatPos: new Vector4(0, 0, 0, 0),
    _updateFrame: 0
};
export class WebGlobalRenderData {
}
var ChildrenUpdateType;
(function (ChildrenUpdateType) {
    ChildrenUpdateType[ChildrenUpdateType["All"] = -1] = "All";
    ChildrenUpdateType[ChildrenUpdateType["Clip"] = 1] = "Clip";
    ChildrenUpdateType[ChildrenUpdateType["Blend"] = 2] = "Blend";
    ChildrenUpdateType[ChildrenUpdateType["Alpha"] = 4] = "Alpha";
    ChildrenUpdateType[ChildrenUpdateType["Pass"] = 8] = "Pass";
    ChildrenUpdateType[ChildrenUpdateType["Global"] = 16] = "Global";
})(ChildrenUpdateType || (ChildrenUpdateType = {}));
export class WebRenderStruct2D {
    get renderMatrix() {
        return this.trans.matrix;
    }
    set renderMatrix(value) {
        if (this.trans) {
            this.trans.matrix = value;
            this.trans.modifiedFrame = Stat.loopCount;
        }
        else {
            this.trans = { matrix: value, modifiedFrame: Stat.loopCount };
        }
    }
    get alpha() {
        return this._alpha;
    }
    set alpha(value) {
        this._alpha = value;
        if (this.parent) {
            this.globalAlpha = this.parent.globalAlpha * value;
        }
        else
            this.globalAlpha = value;
        this.updateChildren(ChildrenUpdateType.Alpha);
    }
    get blendMode() {
        return this._blendMode || this._parentBlendMode || BlendMode.normal;
    }
    set blendMode(value) {
        this._blendMode = value;
        this._updateBlendMode();
        this.updateChildren(ChildrenUpdateType.Blend);
    }
    get renderDataHandler() {
        return this._renderDataHandler;
    }
    set renderDataHandler(value) {
        this._renderDataHandler = value;
        if (value)
            this._renderDataHandler.owner = this;
    }
    get globalRenderData() {
        return this._globalRenderData || this._parentGlobalRenderData;
    }
    set globalRenderData(value) {
        if (value) {
            this._globalShaderData = value.globalShaderData;
        }
        else {
            this._globalShaderData = null;
        }
        this._globalRenderData = value;
        this.updateChildren(ChildrenUpdateType.Global);
    }
    get pass() {
        return this._pass || this._parentPass;
    }
    set pass(value) {
        if (value !== this._pass) {
            this._pass = value;
            if (this._parentPass) {
                value.priority = this._parentPass.priority + 1;
            }
            this.updateChildren(ChildrenUpdateType.Pass);
        }
    }
    constructor() {
        this.zIndex = 0;
        this._effectZ = 0;
        this.stackingRoot = false;
        this.rect = new Rectangle();
        this.renderLayer = 1;
        this.children = [];
        this.renderType = -1;
        this.renderUpdateMask = 0;
        this.dcBounds = new Rectangle();
        this.globalAlpha = 1.0;
        this._alpha = 1.0;
        this._blendMode = BlendMode.invalid;
        this._parentBlendMode = BlendMode.invalid;
        this.needUploadClip = -1;
        this.needUploadAlpha = true;
        this.enabled = true;
        this.isRenderStruct = false;
        this.renderElements = null;
        this.spriteShaderData = null;
        this._globalShaderData = null;
        this._globalRenderData = null;
        this._parentGlobalRenderData = null;
        this._clipRect = null;
        this._parentClipInfo = null;
        this._clipInfo = null;
        this._rnUpdateFun = null;
    }
    setRenderUpdateCallback(func) {
        this._rnUpdateFun = func;
    }
    _handleInterData() {
        let rect = this._clipRect;
        if (rect) {
            let info = this._clipInfo;
            let trans = this.trans;
            let parentClipUpdateFrame = this._parentClipInfo && this._parentClipInfo !== _DefaultClipInfo ? this._parentClipInfo._updateFrame : -1;
            if (trans) {
                if (info._updateFrame < trans.modifiedFrame || info._updateFrame < parentClipUpdateFrame) {
                    let mat = trans.matrix;
                    let cm = info.clipMatrix;
                    let { x, y, width, height } = rect;
                    let tx = mat.tx, ty = mat.ty;
                    cm.tx = x * mat.a + y * mat.c + tx;
                    cm.ty = x * mat.b + y * mat.d + ty;
                    cm.a = width * mat.a;
                    cm.b = width * mat.b;
                    cm.c = height * mat.c;
                    cm.d = height * mat.d;
                    if (parentClipUpdateFrame !== -1) {
                        let parentClipPos = this._parentClipInfo.clipMatPos;
                        let offsetx = parentClipPos.z - parentClipPos.x;
                        let offsety = parentClipPos.w - parentClipPos.y;
                        if (cm.a > 0 && cm.d > 0) {
                            let parentMat = this._parentClipInfo.clipMatrix;
                            let parentMinX = parentMat.tx;
                            let parentMinY = parentMat.ty;
                            let parentMaxX = parentMinX + parentMat.a;
                            let parentMaxY = parentMinY + parentMat.d;
                            let cmaxx = tx + cm.a;
                            let cmaxy = ty + cm.d;
                            if (cmaxx <= parentMinX || cmaxy <= parentMinY || tx >= parentMaxX || ty >= parentMaxY) {
                                cm.a = -0.1;
                                cm.d = -0.1;
                            }
                            else {
                                if (tx < parentMinX) {
                                    cm.a -= (parentMinX - tx);
                                    tx = cm.tx = parentMinX;
                                }
                                if (cmaxx > parentMaxX) {
                                    cm.a -= (cmaxx - parentMaxX);
                                }
                                if (ty < parentMinY) {
                                    cm.d -= (parentMinY - ty);
                                    ty = cm.ty = parentMinY;
                                }
                                if (cmaxy > parentMaxY) {
                                    cm.d -= (cmaxy - parentMaxY);
                                }
                                if (cm.a <= 0)
                                    cm.a = -0.1;
                                if (cm.d <= 0)
                                    cm.d = -0.1;
                            }
                        }
                        tx += offsetx;
                        ty += offsety;
                    }
                    info.clipMatDir.setValue(cm.a, cm.b, cm.c, cm.d);
                    info.clipMatPos.setValue(cm.tx, cm.ty, tx, ty);
                    info._updateFrame = Math.max(trans.modifiedFrame, parentClipUpdateFrame);
                }
            }
        }
        if (this._renderDataHandler) {
            let data = this.spriteShaderData;
            let info = this.getClipInfo();
            if (this.needUploadClip < info._updateFrame) {
                data.setVector(ShaderDefines2D.UNIFORM_CLIPMATDIR, info.clipMatDir);
                data.setVector(ShaderDefines2D.UNIFORM_CLIPMATPOS, info.clipMatPos);
                this.needUploadClip = info._updateFrame;
            }
            if (this.needUploadAlpha) {
                data.setNumber(ShaderDefines2D.UNIFORM_VERTALPHA, this.globalAlpha);
                this.needUploadAlpha = false;
            }
        }
    }
    _updateBlendMode() {
        if (!this.spriteShaderData)
            return;
        BlendModeHandler.setShaderData(this.blendMode, this.spriteShaderData);
    }
    setClipRect(rect) {
        this._clipRect = rect;
        rect ? this._initClipInfo() : this._clipInfo = null;
        this.updateChildren(ChildrenUpdateType.Clip);
    }
    _initClipInfo() {
        if (!this._clipInfo) {
            this._clipInfo = {
                clipMatDir: new Vector4,
                clipMatPos: new Vector4,
                clipMatrix: new Matrix,
                _updateFrame: -1
            };
        }
        else
            this._clipInfo._updateFrame = -1;
    }
    getClipInfo() {
        return this._clipInfo || this._parentClipInfo || _DefaultClipInfo;
    }
    updateChildren(type) {
        let info, blendMode, alpha;
        let priority = 0, pass = null;
        let globalShaderData = null, globalRenderData = null;
        let updateBlend = false, updateClip = false, updateAlpha = false, updatePass = false, updateGlobal = false;
        if (type & ChildrenUpdateType.Clip) {
            info = this.getClipInfo();
            this.needUploadClip = -1;
            updateClip = true;
        }
        if (type & ChildrenUpdateType.Blend) {
            blendMode = this.blendMode;
            updateBlend = true;
        }
        if (type & ChildrenUpdateType.Alpha) {
            alpha = this.globalAlpha;
            this.needUploadAlpha = true;
            updateAlpha = true;
        }
        if (type & ChildrenUpdateType.Pass) {
            pass = this.pass;
            priority = pass ? pass.priority + 1 : 0;
            updatePass = true;
        }
        if (type & ChildrenUpdateType.Global) {
            updateGlobal = true;
            globalShaderData = this._globalShaderData;
            globalRenderData = this._globalRenderData;
        }
        for (const child of this.children) {
            let updateChild = false;
            if (updateClip) {
                child._parentClipInfo = info;
                if (!child._clipInfo) {
                    updateChild = true;
                }
            }
            if (updateBlend) {
                if (child._blendMode === BlendMode.invalid) {
                    child._parentBlendMode = blendMode;
                    child._updateBlendMode();
                    updateChild = true;
                }
            }
            if (updateAlpha) {
                child.globalAlpha = alpha * child.alpha;
                updateChild = true;
            }
            if (updatePass) {
                child._parentPass = pass;
                if (child._pass && child._pass !== pass) {
                    child._pass.priority = priority;
                }
                updateChild = true;
            }
            if (updateGlobal) {
                if (!child._globalRenderData) {
                    updateChild = true;
                    child._globalShaderData = globalShaderData;
                }
                child._parentGlobalRenderData = globalRenderData;
            }
            if (updateChild) {
                child.updateChildren(type);
            }
        }
    }
    setRepaint() {
        if (this.pass) {
            this.pass.repaint = true;
        }
    }
    addChild(child, index) {
        child.parent = this;
        this.children.splice(index, 0, child);
        child._parentClipInfo = this.getClipInfo();
        child._parentBlendMode = this.blendMode;
        child.globalAlpha = this.globalAlpha * child._alpha;
        let parentPass = this.pass;
        child._parentPass = parentPass;
        if (child._pass && parentPass) {
            child._pass.priority = parentPass.priority + 1;
        }
        child._parentGlobalRenderData = this.globalRenderData;
        if (!child._globalRenderData)
            child._globalShaderData = this._globalShaderData;
        child.updateChildren(ChildrenUpdateType.All);
        return;
    }
    updateChildIndex(child, oldIndex, index) {
        if (oldIndex === index)
            return;
        this.children.splice(oldIndex, 1);
        if (index >= this.children.length) {
            this.children.push(child);
        }
        else {
            this.children.splice(index, 0, child);
        }
    }
    removeChild(child) {
        const index = this.children.indexOf(child);
        if (index !== -1) {
            child.parent = null;
            this.children.splice(index, 1);
            child._parentPass = null;
            if (child._pass) {
                child._pass.priority = 0;
            }
            child._parentClipInfo = null;
            child._parentBlendMode = BlendMode.invalid;
            child.globalAlpha = child._alpha;
            child._parentGlobalRenderData = null;
            if (!child._globalRenderData)
                child._globalShaderData = null;
            child.updateChildren(ChildrenUpdateType.All);
        }
    }
    renderUpdate(context) {
        if (this.renderDataHandler) {
            this.renderDataHandler.inheriteRenderData(context);
        }
        if (this._rnUpdateFun) {
            this._rnUpdateFun(context);
        }
    }
    destroy() {
        this._clipInfo = null;
        this._parentClipInfo = null;
        this._clipRect = null;
        this.renderElements = null;
        this.spriteShaderData = null;
        this.parent = null;
        this.children.length = 0;
        this.children = null;
        this.pass = null;
    }
}
